import { Trans, useLingui } from '@lingui/react/macro';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { Mag } from '../components/mag.js';
import { magToast } from '../components/toaster.js';
import { TopNav } from '../components/top-nav.js';
import {
  type AdAccountListEnvelope,
  type CampaignListItem,
  type ConnectedAdAccount,
  type fetchMe,
  fetchProviderCampaigns,
  googleConnectStartUrl,
  importProviderCampaign,
  metaConnectStartUrl,
} from '../lib/api.js';
import { useBrandTitle } from '../lib/document-title.js';
import { founderErrorCopy } from '../lib/error-copy.js';
import { adAccountsQuery, brandsQuery, importedCampaignsQuery, meQuery } from '../lib/queries.js';

// /campaigns/import — Phase A "take over an existing campaign" flow.
// Three states wear the same page chrome:
//   1. No connected account → OAuth launchpad. Two buttons that hand
//      the founder off to the real Google Ads / Meta Ads OAuth flows
//      and bring them back here when finalize lands (returnTo
//      threading in the start route → state token → picker).
//   2. Connected account → campaign picker (proxies the right provider's
//      client; the founder picks one to import).
//   3. Picked → loading → result lives on /campaigns/imported/:id.
// Each transition is in-page (no router navigation between 1 and 2) so
// the founder doesn't lose context.

export const Route = createFileRoute('/brand/$slug/campaigns/import')({
  beforeLoad: async ({ params, context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    const brands = await queryClient.ensureQueryData(brandsQuery());
    const brand = brands.find((b) => b.slug === params.slug);
    if (!brand) throw redirect({ to: '/brands' });
    const envelope = await queryClient.ensureQueryData(adAccountsQuery(brand.id));
    return { me, envelope, brand };
  },
  loader: ({ context }) =>
    context as {
      me: Awaited<ReturnType<typeof fetchMe>>;
      envelope: AdAccountListEnvelope | null;
      brand: { id: string; name: string; slug: string };
    },
  component: ImportPage,
});

function ImportPage() {
  const { t } = useLingui();
  const { envelope, brand } = Route.useLoaderData();
  useBrandTitle(brand.slug, [t`import campaign`]);
  // accounts comes straight from adAccountsQuery; we no longer mutate
  // it locally because the OAuth flow is a full-page handoff (the
  // founder leaves to google/meta, comes back via returnTo). Page
  // mount re-runs the query and the new account appears naturally.
  const accounts: ConnectedAdAccount[] = envelope?.accounts ?? [];

  return (
    <main className="min-h-screen bg-[var(--cream)]">
      <TopNav active="campaigns" />
      <section className="mx-auto max-w-[820px] px-4 pb-16 pt-8 sm:px-7">
        <Link
          to="/brand/$slug/campaigns"
          params={{ slug: brand.slug }}
          className="link-quiet text-[12px]"
        >
          {'↘'} <Trans>back to campaigns</Trans>
        </Link>
        <p className="mt-3 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
          {'↘'} <Trans>campaigns · import</Trans>
        </p>
        <h1 className="mt-2 font-display text-[28px] sm:text-[34px] font-black leading-[1.05] tracking-[-0.04em]">
          <Trans>take over an existing campaign.</Trans>
        </h1>
        <p className="mt-3 text-[14.5px] leading-[1.5] text-[var(--ink)]/70">
          <Trans>
            point me at a campaign in your <b>google ads</b> or <b>meta ads</b> account and i'll
            read its last 30 days, tell you what i see, and queue up the changes i'd make. nothing
            ships until you approve.
          </Trans>
        </p>

        {accounts.length === 0 ? (
          <ConnectStep brandId={brand.id} slug={brand.slug} />
        ) : (
          <PickStep accounts={accounts} brandId={brand.id} slug={brand.slug} />
        )}
      </section>
    </main>
  );
}

// The customer-id paste UI + "mock mode" warning that used to live
// here predated the real OAuth flows landing — both google and meta
// now have working start/callback/handoff/finalize paths under
// /api/brands/:id/{google,meta}/connect/*. The picker pages already
// exist; we just route the founder into them with a returnTo back
// to this surface so they land on the campaign-picker step after
// finalize.

function ConnectStep({ brandId, slug }: { brandId: string; slug: string }) {
  const { t } = useLingui();
  // We pass the current path as returnTo so finalize lands the
  // founder back on this page — the next render reads adAccountsQuery
  // fresh, sees the new connected account, and renders PickStep.
  const returnTo = `/brand/${slug}/campaigns/import`;
  const onConnectGoogle = () => {
    window.location.assign(googleConnectStartUrl(brandId, { returnTo }));
  };
  const onConnectMeta = () => {
    window.location.assign(metaConnectStartUrl(brandId, { bundles: ['meta_paid'], returnTo }));
  };

  return (
    <div className="mt-6 rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--plum)] sm:p-6">
      <div className="flex items-start gap-3">
        <Mag size={48} mood="casting" />
        <div className="flex-1">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
            {'↘'} <Trans>pick the platform</Trans>
          </p>
          <p className="mt-1.5 mag-voice text-[17px] leading-[1.4] text-[var(--ink)]">
            <Trans>
              “sign in with the ad-platform you're already running on and i'll read what's working.
              i never touch anything until you approve.”
            </Trans>
          </p>
          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <ProviderCard
              label={t`Google Ads`}
              hint={t`search · display · youtube · pmax.`}
              onClick={onConnectGoogle}
            />
            <ProviderCard
              label={t`Meta Ads`}
              hint={t`facebook · instagram. paid scope only — organic posting stays separate.`}
              onClick={onConnectMeta}
            />
          </div>
          <div className="mt-4 flex items-center justify-end">
            <Link to="/brand/$slug/campaigns" params={{ slug }} className="link-quiet">
              <Trans>cancel</Trans>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProviderCard({
  label,
  hint,
  onClick,
}: {
  label: string;
  hint: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="card-lift block rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-4 text-left shadow-[3px_3px_0_var(--spell)] hover:shadow-[4px_4px_0_var(--plum)] sm:p-5"
    >
      <p className="font-display text-[15px] font-bold leading-tight tracking-[-0.01em] text-[var(--ink)]">
        <Trans>sign in with {label}</Trans>
      </p>
      <p className="mt-1.5 font-serif text-[12.5px] leading-[1.5] text-[var(--ink)]/65">{hint}</p>
      <p className="mt-3 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--plum)]">
        <Trans>connect</Trans> {'→'}
      </p>
    </button>
  );
}

function PickStep({
  accounts,
  brandId,
  slug,
}: {
  accounts: ConnectedAdAccount[];
  brandId: string;
  slug: string;
}) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const [selectedAccountId, setSelectedAccountId] = useState<string>(accounts[0]?.id ?? '');
  const [campaigns, setCampaigns] = useState<CampaignListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [importingId, setImportingId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  // Already-imported map for this brand + selected ad account. The
  // picker walks the provider's full campaign list, so we need to
  // mark rows that already exist on our side as "already brought
  // in" — otherwise the founder sees the same "take it over" button
  // for a campaign they imported five minutes ago. Key is the
  // provider's campaign id, value is the magig campaign id (for
  // deep-linking to the takeover view).
  const { data: importedEnvelope } = useQuery(importedCampaignsQuery());
  const importedByProviderId = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of importedEnvelope?.campaigns ?? []) {
      if (c.brandId !== brandId) continue;
      if (c.adAccountId !== selectedAccountId) continue;
      map.set(c.providerCampaignId, c.id);
    }
    return map;
  }, [importedEnvelope, brandId, selectedAccountId]);
  // Distinguishes "fetch failed" from "fetch succeeded but returned
  // 0 rows". Without this the empty state read as "no campaigns in
  // this account" even when the api blew up with a 400/500 — no
  // way for the founder to retry short of refreshing the whole
  // page. fetchAttempt bumps when "try again" is clicked so the
  // effect re-runs without depending on selectedAccountId changing.
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [fetchAttempt, setFetchAttempt] = useState(0);

  // Fetch campaign list whenever the picker switches accounts.
  // biome-ignore lint/correctness/useExhaustiveDependencies: fetchAttempt is a deliberate re-run trigger — bumped by "try again" to refetch without selectedAccountId changing (see comment above).
  useEffect(() => {
    if (!selectedAccountId) return;
    let cancelled = false;
    setLoading(true);
    setFetchError(null);
    fetchProviderCampaigns(selectedAccountId)
      .then((c) => {
        if (cancelled) return;
        setCampaigns(c);
      })
      .catch((err) => {
        if (cancelled) return;
        const message = founderErrorCopy(err, 'check your account id.');
        setCampaigns([]);
        setFetchError(message);
        magToast.error(t`couldn't load campaigns.`, message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedAccountId, fetchAttempt]);

  const handleImport = async (providerCampaignId: string) => {
    if (!selectedAccountId) return;
    setImportingId(providerCampaignId);
    try {
      const imported = await importProviderCampaign({
        adAccountId: selectedAccountId,
        providerCampaignId,
      });
      navigate({
        to: '/brand/$slug/campaigns/imported/$id',
        params: { slug, id: imported.id },
      });
    } catch (err) {
      magToast.error("mag couldn't import.", founderErrorCopy(err, 'try a different campaign.'));
      setImportingId(null);
    }
  };

  return (
    <div className="mt-6 flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          {'↘'} <Trans>connected accounts</Trans>
        </span>
        {accounts.map((a) => {
          const active = a.id === selectedAccountId;
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => setSelectedAccountId(a.id)}
              className={`rounded-full px-3 py-1 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] ${
                active
                  ? 'border-[1.5px] border-[var(--ink)] bg-[var(--spell)] text-[var(--ink)]'
                  : 'border border-[var(--line)] bg-white text-[var(--ink)]/70 hover:text-[var(--ink)]'
              }`}
            >
              {a.accountLabel}
              {a.status === 'mock' ? (
                <span className="ml-2 text-[9px] tracking-[0.12em] opacity-70">
                  <Trans>MOCK</Trans>
                </span>
              ) : null}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => setShowAdd((v) => !v)}
          className="link-quiet text-[12px]"
        >
          {'↘'} {showAdd ? t`hide` : t`add another`}
        </button>
      </div>

      {/* "add another" — OAuth launchpad. Connecting is a full-page
          handoff now (Google/Meta OAuth dance), so we don't get an
          in-page callback. The picker comes back to this surface via
          returnTo, accounts re-fetches via adAccountsQuery, and the
          new account appears at the head of the list. */}
      {showAdd ? <ConnectStep brandId={brandId} slug={slug} /> : null}

      <div className="rounded-2xl border border-[var(--line)] bg-white p-1">
        {loading ? (
          <div className="flex items-center gap-3 px-4 py-6">
            <Mag size={26} mood="casting" />
            <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--ink)]/65">
              <Trans>mag is fetching campaigns…</Trans>
            </p>
          </div>
        ) : fetchError ? (
          <div className="flex flex-col gap-3 px-4 py-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.12em] text-[var(--pulse)]">
                {'↘'} <Trans>couldn't load campaigns</Trans>
              </p>
              <p className="mt-1 font-serif text-[13.5px] leading-[1.4] text-[var(--ink)]/75">
                {fetchError}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setFetchAttempt((n) => n + 1)}
              className="btn btn-sm self-end sm:self-auto"
            >
              <Trans>try again</Trans>
              <span className="btn-arrow">→</span>
            </button>
          </div>
        ) : campaigns.length === 0 ? (
          <p className="px-4 py-6 font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--ink)]/55">
            {'↘'} <Trans>no campaigns in this account.</Trans>
          </p>
        ) : (
          <ul className="flex flex-col">
            {campaigns.map((c) => {
              const alreadyImportedId = importedByProviderId.get(c.providerCampaignId);
              return (
                <li
                  key={c.providerCampaignId}
                  className="border-b border-[var(--line)] last:border-0"
                >
                  <CampaignRow
                    campaign={c}
                    importing={importingId === c.providerCampaignId}
                    disabled={importingId !== null && importingId !== c.providerCampaignId}
                    alreadyImportedId={alreadyImportedId}
                    slug={slug}
                    onImport={() => handleImport(c.providerCampaignId)}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function CampaignRow({
  campaign,
  importing,
  disabled,
  alreadyImportedId,
  slug,
  onImport,
}: {
  campaign: CampaignListItem;
  importing: boolean;
  disabled: boolean;
  /** Magig campaign id if this provider campaign is already on our
   *  side. Swaps the "take it over" CTA for a deep-link into the
   *  existing takeover view so the founder can't re-import (which
   *  would create a duplicate brand campaign on next reload). */
  alreadyImportedId: string | undefined;
  slug: string;
  onImport: () => void;
}) {
  const { t } = useLingui();
  // Each value gets a NAME before it reaches the message. A member
  // expression inside <Trans> extracts as {0}/{1}/{2}, and a translator
  // reordering an unnamed placeholder is guessing at what it holds.
  const spent30d = campaign.costUsd30d.toFixed(2);
  const kind = campaign.type;
  const runState = campaign.status;
  const dailyBudget = campaign.dailyBudgetUsd;
  return (
    <div className="flex items-center gap-4 px-4 py-3">
      <div className="flex-1">
        <p className="text-[14px] font-bold tracking-[-0.01em] text-[var(--ink)]">
          {campaign.name}
        </p>
        <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--ink)]/55">
          {/* Wave 6 owns the currency here — the account's real currency is
              not on ad_accounts yet, so the $ stays hardcoded for now and
              the message is shaped so a currency placeholder can replace
              it without a re-translation. */}
          <Trans>
            {kind} · {runState} · ${dailyBudget}/day · spent ${spent30d} last 30d
          </Trans>
          {alreadyImportedId ? (
            <>
              {' · '}
              <Trans>already imported</Trans>
            </>
          ) : null}
        </p>
      </div>
      {alreadyImportedId ? (
        <Link
          to="/brand/$slug/campaigns/imported/$id"
          params={{ slug, id: alreadyImportedId }}
          className="btn btn-ghost text-[11px]"
        >
          <Trans>open take-over</Trans>
          <span className="btn-arrow">→</span>
        </Link>
      ) : (
        <button
          type="button"
          onClick={onImport}
          disabled={importing || disabled}
          className="btn btn-ghost text-[11px] disabled:opacity-50"
        >
          {importing ? t`mag is reading…` : t`take it over`}
          <span className="btn-arrow">→</span>
        </button>
      )}
    </div>
  );
}
